/**
 * Shared formatter for the patient-shared context (imported CMR snapshot + any
 * prior CareStart self-assessment) that a provider redeems via a share code.
 *
 * Two blocks, deliberately kept apart:
 *
 *   - THE CHART RECORD (medications, allergies, PMH, PSH). Shared from the patient's own
 *     record with their consent. callLLMAPI wraps this one as AUTHORITATIVE and follows it
 *     with a hardcoded precedence rule, so it outranks whatever the patient says during the
 *     encounter. Patients misreport allergies and drug names constantly; the chart wins.
 *   - THE PATIENT'S OWN ANSWERS BEFORE THE VISIT: a CareStart self-assessment, a
 *     VisitStart pre-visit intake, or both. Patient-reported, so they must NEVER
 *     land inside the authoritative wrapper. For a VisitStart guest the intake is
 *     the only block there is, since they have no chart to share.
 *
 * These used to be concatenated into a single string. Splitting them is the whole point:
 * promoting a patient's self-report to chart standing is precisely the failure the
 * precedence rule exists to prevent.
 *
 * Input shape is the object returned by consumeShareCodeAndFetchRecord /
 * redeemHealthRecordShareToken: { patientName, patientDob, allowedSections,
 * record, careStartResult, visitStartIntake }.
 */

import { differenceInYears, isValid, parseISO } from "date-fns";

import { getCapabilityLabel } from "constants/diagnosticCapabilities";
import { djb2 } from "utils/hash";

// Stored as slugs by the Sex select on DemographicsPage. Mapped rather than capitalized so
// "prefer-not-to-say" does not render as "Prefer-not-to-say". Shared with SharedRecordSummary
// so the prompt and the screen never disagree about what the record says.
export const SEX_LABELS = {
  male: "Male",
  female: "Female",
  other: "Other",
  "prefer-not-to-say": "Prefer not to say",
};

export const sexLabel = (sex) => (sex ? SEX_LABELS[String(sex).toLowerCase()] || sex : null);

/**
 * Age in whole years from a stored "yyyy-MM-dd" date of birth, or null if the date is
 * missing, unparseable, or absurd. Shared with SharedRecordSummary so the screen and the
 * prompt never disagree about how old the patient is.
 */
export const ageFromDob = (dob) => {
  if (!dob) return null;
  const parsed = parseISO(String(dob).trim());
  if (!isValid(parsed)) return null;
  const age = differenceInYears(new Date(), parsed);
  return age >= 0 && age < 150 ? age : null;
};

/**
 * "1990-04-02 (age 36)".
 *
 * The age is spelled out because models do not reliably subtract two dates: asked how old
 * this patient is, they came back a couple of years off, and an age that is wrong by two
 * years is wrong in the direction of a different differential and a different dose. The
 * date of birth stays in front of it, so a reader can still check the arithmetic.
 */
export const dobWithAge = (dob) => {
  const text = String(dob ?? "").trim();
  if (!text) return null;
  const age = ageFromDob(text);
  return age == null ? text : `${text} (age ${age})`;
};

// DemographicsPage labels the field "Weight (lb)", so a bare number is pounds. Left alone if
// the patient already typed a unit.
export const weightLabel = (weight) => {
  if (weight == null || weight === "") return null;
  const text = String(weight).trim();
  return /[a-z]/i.test(text) ? text : `${text} lb`;
};

// `other` history keys as the patient's record stores them. Anything unlisted falls back to a
// de-camel-cased label rather than reaching the prompt as a raw key like "smokingStatus".
const OTHER_LABELS = {
  smokingStatus: "Smoking",
  alcoholUse: "Alcohol use",
  drugUse: "Drug use",
  occupation: "Occupation",
  familyHistory: "Family history",
};

const humanizeKey = (key) =>
  String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

/**
 * Lower-cases a leading capital so a qualifier reads as part of the phrase ("As needed" ->
 * "as needed", "Rash" -> "rash"). Acronyms and dosing shorthand keep their case, because
 * anything with a capital after the first character is not a normally-capitalized word:
 * "IV", "PRN", "BID" pass through untouched.
 */
export const decapitalize = (value) => {
  if (!value) return value;
  const rest = value.slice(1);
  return rest === rest.toLowerCase() ? value.charAt(0).toLowerCase() + rest : value;
};

/** Terminates free text so a trailing note does not run into whatever follows it. */
const sentence = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  return /[.!?]$/.test(text) ? text : `${text}.`;
};

// Condition names out of a DDX result (new + legacy shapes) for the CareStart summary block.
function ddxConditionNames(ddx, limit = 5) {
  if (!ddx) return [];
  const lists = [ddx.diagnoses, ddx.primary_diagnoses, ddx.alternative_diagnoses];
  const names = [];
  for (const list of lists) {
    if (Array.isArray(list)) {
      for (const d of list) {
        const name = d?.name || d?.condition;
        if (name && !names.includes(name)) names.push(name);
      }
    }
  }
  return names.slice(0, limit);
}

/**
 * The patient's chart record, rendered as a text block.
 *
 * This is the authoritative half. It carries the list of sections the patient actually
 * consented to share, because the model needs that to tell "no allergies" apart from
 * "the allergies section was not shared" — the caveat itself lives in the hardcoded
 * precedence rule server-side, not here.
 *
 * FORMAT MATTERS MORE THAN IT LOOKS. This used to be one semicolon-joined line per section,
 * e.g. "Allergies: Penicillin - Rash; Peanuts - Swelling of the lips and throat." and
 * "Past surgical history: Appendectomy 2008". Models copy the shape of their input, so the
 * chart note came back as a single run-on paragraph reading "PSH: Appendectomy 2008" — the
 * bare year stranded with no word saying it was a year, and the whole history unreadable as
 * a paragraph. One item per line, with each qualifier phrased in words.
 *
 * Allergies lead because they are the one section that changes what is safe to prescribe.
 *
 * Returns "" when nothing meaningful was shared, so callers can concat blindly.
 */
export function buildPatientRecordBlock(ctx) {
  if (!ctx?.record) return "";
  const { record, patientName, patientDob, allowedSections = [] } = ctx;

  const blocks = [];

  const identity = [];
  if (patientName || patientDob) {
    // "(DOB 1986-04-02, age 39)" — the age spelled out for the reason in dobWithAge,
    // but comma-joined here rather than reusing it, so the DOB does not end up inside
    // two nested sets of parentheses.
    const age = ageFromDob(patientDob);
    const dob = patientDob
      ? ` (DOB ${patientDob}${age != null ? `, age ${age}` : ""})`
      : "";
    identity.push(`Patient: ${patientName || "name not shared"}${dob}`);
  }
  if (record.demographics) {
    const { sex, weight } = record.demographics;
    if (sexLabel(sex)) identity.push(`Sex: ${sexLabel(sex)}`);
    if (weightLabel(weight)) identity.push(`Weight: ${weightLabel(weight)}`);
  }
  if (identity.length) blocks.push(identity.join("\n"));

  // One heading, then one item per line. `pick` returns the whole line for an entry, so a
  // section that renders every entry blank contributes nothing rather than a bare heading —
  // an empty heading reads as "none known", which is the failure this whole module exists
  // to prevent.
  const addSection = (heading, items, pick, emptyLine = null) => {
    if (!Array.isArray(items)) return;
    if (items.length === 0) {
      // Shared and genuinely empty, which is real information and not the same as withheld.
      if (emptyLine) blocks.push(`${heading}\n- ${emptyLine}`);
      return;
    }
    const rendered = items.map(pick).filter(Boolean);
    if (rendered.length) blocks.push([heading, ...rendered.map((r) => `- ${r}`)].join("\n"));
  };

  addSection(
    "ALLERGIES",
    record.allergies,
    (a) => {
      if (typeof a === "string") return a.trim() || null;
      const name = a?.allergen || a?.name;
      if (!name) return null;
      const reaction = a?.reaction
        ? ` (reaction: ${decapitalize(String(a.reaction).trim())})`
        : "";
      const note = sentence(a?.notes);
      return `${name}${reaction}${note ? `. ${note}` : ""}`;
    },
    "No known allergies (the patient shared this section and it is empty)",
  );

  addSection(
    "MEDICATIONS",
    record.medications,
    (m) => {
      if (typeof m === "string") return m.trim() || null;
      const name = m?.name || m?.medicationName;
      if (!name) return null;
      const dose = m?.dosage ? ` ${String(m.dosage).trim()}` : "";
      const freq = m?.frequency ? `, ${decapitalize(String(m.frequency).trim())}` : "";
      return `${name}${dose}${freq}`;
    },
    "No medications (the patient shared this section and it is empty)",
  );

  addSection("PAST MEDICAL HISTORY", record.conditions, (c) => {
    if (typeof c === "string") return c.trim() || null;
    const name = c?.condition || c?.name || c?.diagnosis;
    if (!name) return null;
    const note = sentence(c?.notes);
    return `${name}${note ? `. ${note}` : ""}`;
  });

  addSection("PAST SURGICAL HISTORY", record.surgical, (s) => {
    if (typeof s === "string") return s.trim() || null;
    const name = s?.procedure || s?.name;
    if (!name) return null;
    // "Appendectomy in 2008", never "Appendectomy 2008" — the model repeats the bare number
    // into the note and the reader is left guessing what it is.
    const when = s?.year ? ` in ${String(s.year).trim()}` : "";
    const note = sentence(s?.notes);
    return `${name}${when}${note ? `. ${note}` : ""}`;
  });

  if (record.other && typeof record.other === "object") {
    const entries = Object.entries(record.other)
      .filter(([, v]) => v && String(v).trim() !== "")
      .map(([k, v]) => `- ${OTHER_LABELS[k] || humanizeKey(k)}: ${String(v).trim()}`);
    if (entries.length) blocks.push(["OTHER HISTORY", ...entries].join("\n"));
  }

  if (!blocks.length) return "";

  return [
    `Sections the patient consented to share: ${allowedSections.join(", ") || "not specified"}`,
    ...blocks,
  ].join("\n\n");
}

/**
 * The prior CareStart self-assessment the patient completed before this encounter.
 *
 * Patient-reported throughout. Included so the model sees the patient's own symptom
 * narrative, framed as reference so it informs rather than just echoes the fresh
 * differential. Never authoritative.
 */
/**
 * The VisitStart pre-visit intake, as lines for the self-report block.
 *
 * Deliberately narrower than what the provider sees on screen. The intake also
 * carries a national ID number, a referral letter and SHA coverage answers; those
 * are what the front desk needs to register someone, and none of them tell a model
 * anything about why the patient is here. An ID number in a prompt is identity with
 * no clinical value, so it does not go in.
 *
 * Sex and date of birth are the exception on that screen. They sit beside the ID
 * number in the same identity answer and were being discarded with it, but unlike
 * it they change clinical reasoning — dosing, pregnancy, and which conditions
 * belong on a differential at all. They come in; the ID number and type stay out.
 *
 * The question text comes from inside each answer — the grouped and sequential
 * screens save what they asked — so this does not need the element catalog and the
 * two cannot drift apart.
 */
function visitStartIntakeLines(intake) {
  if (!intake) return [];
  const answers = intake.elementAnswers || {};
  const lines = [];

  // The identity screen asks for these whenever the chart does not already hold
  // them, so for a guest — who has no chart at all — this is the ONLY place they
  // ever reach a prompt. For a signed-in patient the chart carries them too and
  // the precedence rule settles any disagreement, which is why the duplication is
  // safe and dropping them was not.
  const identity = answers.identity || {};
  const demographics = [
    sexLabel(identity.sex) && `Sex: ${sexLabel(identity.sex)}`,
    dobWithAge(identity.dateOfBirth) &&
      `Date of birth: ${dobWithAge(identity.dateOfBirth)}`,
  ].filter(Boolean);
  if (demographics.length) {
    lines.push(
      `Demographics the patient gave at check-in:\n${demographics
        .map((row) => `- ${row}`)
        .join("\n")}`,
    );
  }

  const complaint = (answers["chief-complaint"] || {}).confirmedSummary;
  if (sentence(complaint)) {
    lines.push(`Reason for the visit, in the patient's own words:\n${sentence(complaint)}`);
  }

  // For a guest these two are the ONLY source: they have no chart, so nothing
  // reaches the authoritative block. Phrased as "listed" rather than as a chart
  // section, because that is exactly what they are.
  const meds = (answers["medication-list"] || {}).medications;
  if (Array.isArray(meds) && meds.length) {
    lines.push(
      `Medications the patient listed at check-in:\n${meds
        .map((m) =>
          [m?.name, m?.dosage, m?.frequency].filter(Boolean).join(", "),
        )
        .filter(Boolean)
        .map((row) => `- ${row}`)
        .join("\n")}`,
    );
  } else if ((answers["medication-list"] || {}).takingMedications === false) {
    lines.push("The patient said at check-in that they take no regular medications.");
  }

  const allergies = (answers.allergies || {}).allergies;
  if (Array.isArray(allergies) && allergies.length) {
    lines.push(
      `Allergies the patient listed at check-in:\n${allergies
        .map((a) =>
          [a?.allergen, a?.reaction && decapitalize(a.reaction)]
            .filter(Boolean)
            .join(" - "),
        )
        .filter(Boolean)
        .map((row) => `- ${row}`)
        .join("\n")}`,
    );
  }

  // Every screen that saves {answers:[...]} — the danger-sign and maternal
  // questions, and whichever screens the clinic enabled. Only the yeses: a wall of
  // "no" is the model's problem to skim, and the yeses are the reason to ask.
  const flagged = [];
  Object.values(answers).forEach((answer) => {
    if (!answer || !Array.isArray(answer.answers)) return;
    answer.answers.forEach((row) => {
      if (row && row.value === "yes" && row.question) flagged.push(row.question);
    });
  });
  if (flagged.length) {
    lines.push(
      `Screening questions the patient answered yes to:\n${flagged
        .map((q) => `- ${q}`)
        .join("\n")}`,
    );
  }

  // What they would not or could not answer. The provider asking in person is the
  // whole reason these are recorded.
  const unanswered = (intake.clarifyingQuestions || [])
    .map((q) => q && q.question)
    .filter(Boolean);
  if (unanswered.length) {
    lines.push(
      `The patient preferred not to answer, or was unsure about:\n${unanswered
        .map((q) => `- ${q}`)
        .join("\n")}`,
    );
  }

  return lines;
}

export function buildPatientSelfReportBlock(ctx) {
  const careStartResult = ctx?.careStartResult;
  const visitStartIntake = ctx?.visitStartIntake;
  if (!careStartResult && !visitStartIntake) return "";

  const lines = [];
  if (careStartResult?.careLevel) {
    lines.push(`Suggested care level: ${careStartResult.careLevel}`);
  }
  const csConditions = ddxConditionNames(careStartResult?.ddxResult);
  if (csConditions.length) {
    lines.push(`Conditions considered: ${csConditions.join("; ")}`);
  }
  if (Array.isArray(careStartResult?.diagnosticNeeds) && careStartResult.diagnosticNeeds.length) {
    lines.push(
      `Suggested tests: ${careStartResult.diagnosticNeeds
        .map((k) => getCapabilityLabel(k) || k)
        .join(", ")}`,
    );
  }
  const symptoms = (careStartResult?.finalTranscript || "").trim();
  if (symptoms) {
    lines.push(`Symptoms the patient reported to CareStart:\n${symptoms}`);
  }

  lines.push(...visitStartIntakeLines(visitStartIntake));

  return lines.length ? lines.join("\n") : "";
}

/**
 * Build the `patientContext` payload every ChartMind LLM call passes to invokeLLMPrompt.
 *
 * callLLMAPI renders `record` into the system prompt behind an AUTHORITATIVE wrapper plus
 * the hardcoded precedence rule, and `selfReport` as a separate patient-reported block.
 * Returns null when there is nothing to send, so callers can spread it unconditionally.
 */
export function buildPatientContextPayload(ctx) {
  const record = buildPatientRecordBlock(ctx);
  const selfReport = buildPatientSelfReportBlock(ctx);
  if (!record && !selfReport) return null;
  return {
    ...(record && { record }),
    ...(selfReport && { selfReport }),
  };
}

/**
 * Short, stable fingerprint of a payload from buildPatientContextPayload, for step
 * dedup/generation keys.
 *
 * Every ChartMind step caches on its inputs. Without the patient in the key, importing a
 * record mid-encounter leaves already-generated steps sitting on their record-blind
 * results. Keyed on the rendered blocks rather than the source object, so a re-import of
 * the same record does not needlessly re-fire every step.
 */
export function patientContextPayloadKey(payload) {
  if (!payload) return "";
  return `${djb2(payload.record || "")}:${djb2(payload.selfReport || "")}`;
}
